import { API } from "apis";
import PageTitle from "components/common/PageTitle";
import CorpInfoForms from "components/corp/info/CorpInfoForms";
import CorpInfoFormsConfirm from "components/corp/info/CorpInfoFormsConfirm";
import ConfirmDialog from "components/dialog/ConfirmDIalog";
import { ApplyStatusType, JOHOKU_ACCOUNT } from "enums";
import { usePrompt } from "hooks/usePrompt";
import {
  ContextModel,
  CorpInfoModel,
  IndustryMstModel,
  JohokuBranchMstModel,
} from "models";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Utils } from "utils";

const CorpInfoPage: React.FC = () => {
  const ctx = useOutletContext<ContextModel>();
  const didEffect = React.useRef(false);

  const industryMst = React.useRef<IndustryMstModel[]>([]);
  const branchMst = React.useRef<JohokuBranchMstModel[]>([]);

  const [data, setData] = React.useState<CorpInfoModel>();
  const [formIsDirty, setFormIsDirty] = React.useState(true);
  const [confirm, setConfirm] = React.useState(false);
  const [confirmResult, setConfirmResult] = React.useState<boolean | null>(null); 
  const [confirmDialog, setConfirmDialog] = React.useState<{
    message: string;
    callback: (result: boolean) => void;
  } | null>(null);

  const navigate = useNavigate();

  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    // ここに設定する文字列は表示には影響しない？
    e.returnValue = "check";
  };

  usePrompt("入力中の内容は破棄されます。よろしいですか？", formIsDirty);

  const _addApplyPrefix = (data: CorpInfoModel) => {
    // 属性名に"apply_"を付与
    const tmp: any = {};
    for (const k in data) {
      if (/^apply_.+/.test(k)) {
        tmp[k] = (data as any)[k];
      } else {
        const _ = (data as any)[k];
        tmp["apply_" + k] = _;
        if (Array.isArray(_)) {
          tmp["apply_" + k] = [];
          for (const item of _) {
            const tmp2: any = {};
            for (const kk in item) {
              tmp2["apply_" + kk] = item[kk];
            }
            tmp["apply_" + k].push(tmp2);
          }
        }
      }
    }
    return tmp;
  };

  const onAddressSearch = async () => {
    if (!data) {
      return;
    }
    ctx.loading.setState(true);
    try {
      const response = await API.Address.address(data.post_no1,data.post_no2);
      console.log(response);
      data.prefecture_cd = response.data.prefecture_cd ?? '';
      data.citytown = response.data.address ?? '';
      data.address = '';
      setData({ ...data });
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  const onToConfirm = async () => {
    if (!data) {
      return;
    }

    if (data.invoice_no === 'T') {
      data.invoice_no = '';
    }

    if(data.bank_account_list.length) {
      const is_JHK =  data.bank_account_list.some(item => item.bank_cd === JOHOKU_ACCOUNT.bank_cd);
      data.has_johoku_account = is_JHK;
    }

    // 法人個人区分が個人の場合は、法人番号をクリアする
    if (data.is_personal) {
      data.corp_no = "";
    }

    // 属性名に"apply_"を付与
    const tmp = _addApplyPrefix(data);

    ctx.loading.setState(true);
    try {
      const response = await API.ApplyCorp.updateCheck(tmp);
      console.log(response);
      setConfirm(true);
      refreshBankAccount();
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    } catch (err) {
      Utils.errorAction(ctx, err);
      handlerErrorBankAccount(err);
    }
    ctx.loading.setState(false);
  };

  const onBack = () => {
    setConfirm(false);
  };
  const onNext = async () => {
    if (!data) {
      return;
    }
    if (data.invoice_no === 'T') {
      data.invoice_no = '';
    }

    if(data.bank_account_list.length) {
      const is_JHK =  data.bank_account_list.some(item => item.bank_cd === JOHOKU_ACCOUNT.bank_cd);
      data.has_johoku_account = is_JHK;
    }

    setFormIsDirty(false);

    // 属性名に"apply_"を付与
    const tmp = _addApplyPrefix(data);

    ctx.loading.setState(true);
    try {
      const response = await API.ApplyCorp.update(tmp);
      console.log(response);

      ctx.msgDialog.setState({
        message: "会社情報の更新を申請しました。",
        callback: () => navigate("/mypage/"),
      });
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  const onCancel = () => {
    setConfirmDialog({
      message: "この申請を取り消します。よろしいですか？",
      callback: async (result: boolean) => {
        setConfirmDialog(null);
        if (!result) {
          return;
        }

        setFormIsDirty(false);
        ctx.loading.setState(true);
        try {
          const response = await API.ApplyCorp.del();
          console.log(response);

          navigate(0)
        } catch (err) {
          Utils.errorAction(ctx, err);
        }
        ctx.loading.setState(false);
      },
    });
  };

  const handlerErrorBankAccount = (errors: any) => {
    if(!errors || !data) return;
    if (errors && errors.response?.status === 400 && !!data.bank_account_list.length) {
      let tmpBank = {} as any;

      data.bank_account_list.forEach((bank) => {
        let keyString = `${bank["account_type"]}|${bank["account_no"]}|${bank["branch_cd"]}|${bank["bank_cd"]}`;

        if (tmpBank[keyString]) {
          tmpBank[keyString].is_duplicate = true;
          bank.is_duplicate = true;
        } else {
          tmpBank[keyString] = bank;
          bank.is_duplicate = false;
        }
      });
      setData({ ...data });
    }
  };

  const refreshBankAccount = () => {
    if(!data) return;

    data.bank_account_list.forEach((bank) => bank.is_duplicate = false);
    setData({ ...data })
  };

  React.useEffect(() => {
    if (didEffect.current) {
      return;
    }
    didEffect.current = true;
    const f = async () => {
      ctx.loading.setState(true);
      await ctx.auth.initialize();
      try {
        {
          const response = await API.IndustryMst.list();
          console.log(response);
          industryMst.current = response.data;
        }
        {
          const response = await API.JohokuBranchMst.list();
          console.log(response);
          branchMst.current = response.data;
        }
        {
          const response = await API.Corp.info();
          console.log(response);
          setData({...response.data, order_limit: +response.data.order_limit / 10000 });

          if (response.data.apply_status === ApplyStatusType._0_WAIT) {
            setFormIsDirty(false);
          }
        }
      } catch (err) {
        Utils.errorAction(ctx, err);
      }
      ctx.loading.setState(false);
    };
    f();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return <></>;
  }

  return (
    <div className="w-full px-12">
      <Helmet>
        <title>会社情報設定</title>
      </Helmet>
      {!confirm && (
        <>
          <PageTitle title="会社情報設定" className="py-2" />
          {data.apply_status === ApplyStatusType._2_REPUDIATION && (
            <div className="mb-4 border p-4">
              <div className="text-red-600 mb-4">
                ※&nbsp;この会社情報申請は管理会社によって否認されました。否認理由を確認してください。
              </div>
              {/* <div className="">
                否認理由&nbsp;:&nbsp;{data.apply_deny_reason}
              </div> */}
              <div className="">コメント&nbsp;:&nbsp;{data.apply_comment}</div>
            </div>
          )}
          <CorpInfoForms
            isUpdate={true}
            data={data}
            setData={setData}
            industryMst={industryMst.current}
            branchMst={branchMst.current}
            onAddressSearch={onAddressSearch}
          />
          {data.apply_status === ApplyStatusType._2_REPUDIATION && (
            <div className="flex justify-center m-8">
              <button className="gray px-4 py-2" onClick={onCancel} disabled={confirmResult === true} >
                申請取消
              </button>

            </div>
          )}
          <div className="flex justify-center m-8">
            {data.apply_status !== ApplyStatusType._0_WAIT && (
              <button className="primary px-4 py-2" onClick={onToConfirm}>
                入力内容の確認
              </button>
            )}
          </div>
        </>
      )}
      {confirm && (
        <>
          <div className="mt-8">以下の内容で会社情報の変更を申請します。</div>
          <CorpInfoFormsConfirm data={data} industryMst={industryMst.current} />
          <div className="flex justify-center gap-4 m-8">
            <button className="primary px-4 py-2" onClick={onNext}>
              申請する
            </button>
            <button className="secondary px-4 py-2" onClick={onBack}>
              戻る
            </button>
          </div>
        </>
      )}

      <ConfirmDialog
        modalIsOpen={!!confirmDialog}
        message={confirmDialog?.message ?? ""}
        action={(result: boolean) => {
          if (confirmDialog?.callback) {
            confirmDialog.callback(result);
            setConfirmResult(result)
          }
        }}
      />
    </div>
  );
};
export default CorpInfoPage;
